import moment from 'moment';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { HilbiWidgetViews } from '@store/user-interface/type';
import { LANG_EN_SLUG } from '@common/types/common';
import {
  TInitUserForm,
  TSelectExpert,
  TSelectMedicalInstitution,
  TSetCurrentStep,
  TSetDate,
  TSetLanguage,
  TSetNote,
  TSetWidgetOpen,
  TUserInterfaceState,
  TUpdateLeadToken,
  TSetUserForm,
  TSetYearAndMonth,
  TSetDay,
  TSelectSlot,
  TSetNextStepDisabled,
} from '@custom-types/user-interface';

const defaultState: TUserInterfaceState = {
  widgetOpen: false,
  currentLang: LANG_EN_SLUG,
  currentStep: HilbiWidgetViews.Search,
  nextStepDisabled: true,
  order: {
    expert: {
      id: null,
      name: null,
    },
    medicalInstitution: {
      id: null,
      name: null,
      address: null,
    },
    expertise: {
      id: null,
      name: null,
    },
    datetime: {
      year: moment().format('YYYY'),
      month: moment().format('MM'),
      day: moment().format('DD'),
      time: null,
    },
    note: null,
    eventId: null,
    event: null,
  },
  form: {
    name: null,
    email: null,
    phone: null,
    insuranceCompany: null,
    leadToken: null,
  },
  dayClicked: false,
};

const userInterfaceSlice = createSlice({
  name: 'userInterface',
  initialState: defaultState,
  reducers: {
    initUserForm: (
      state: TUserInterfaceState,
      action: PayloadAction<TInitUserForm>,
    ): TUserInterfaceState => ({
      ...state,
      form: {
        ...state.form,
        email: action.payload.email,
        leadToken: action.payload.leadToken,
      },
    }),
    updateLeadToken: (
      state: TUserInterfaceState,
      action: PayloadAction<TUpdateLeadToken>,
    ): TUserInterfaceState => ({
      ...state,
      form: {
        ...state.form,
        leadToken: action.payload.leadToken,
      },
    }),
    setUserForm: (
      state: TUserInterfaceState,
      action: PayloadAction<TSetUserForm>,
    ): TUserInterfaceState => ({
      ...state,
      form: {
        ...state.form,
        name: action.payload.name,
        phone: action.payload.phoneNumber,
        insuranceCompany: action.payload.insuranceCompany,
        leadToken: action.payload.leadToken,
      },
    }),
    selectExpert: (
      state: TUserInterfaceState,
      action: PayloadAction<TSelectExpert>,
    ) => ({
      ...state,
      nextStepDisabled: false,
      order: {
        ...defaultState.order,
        expert: { id: action.payload.id, name: action.payload.name },
      },
    }),
    selectMedicalInstitution: (
      state: TUserInterfaceState,
      action: PayloadAction<TSelectMedicalInstitution>,
    ): TUserInterfaceState => ({
      ...state,
      nextStepDisabled: false,
      order: {
        ...state.order,
        medicalInstitution: {
          id: action.payload.medicalInstitution.id,
          name: action.payload.medicalInstitution.name,
          address: action.payload.medicalInstitution.address,
        },
        expertise: {
          id: action.payload.expertise.id,
          name: action.payload.expertise.name,
        },
      },
    }),
    setYearAndMonth: (
      state: TUserInterfaceState,
      action: PayloadAction<TSetYearAndMonth>,
    ): TUserInterfaceState => {
      return {
        ...state,
        order: {
          ...state.order,
          datetime: {
            ...state.order.datetime,
            year: action.payload.year,
            month: action.payload.month,
            day: '01',
            time: null,
          },
        },
      };
    },
    setDay: (
      state: TUserInterfaceState,
      action: PayloadAction<TSetDay>,
    ): TUserInterfaceState => {
      return {
        ...state,
        order: {
          ...state.order,
          datetime: {
            ...state.order.datetime,
            day: action.payload.day,
            time: null,
          },
        },
      };
    },
    setDate: (
      state: TUserInterfaceState,
      action: PayloadAction<TSetDate>,
    ): TUserInterfaceState => {
      const splittedDate = action.payload.date.split('-');
      return {
        ...state,
        nextStepDisabled: true,
        order: {
          ...state.order,
          datetime: {
            ...state.order.datetime,
            year: splittedDate[0],
            month: splittedDate[1],
            day: splittedDate[2],
            time: null,
          },
        },
      };
    },
    selectSlot: (
      state: TUserInterfaceState,
      action: PayloadAction<TSelectSlot>,
    ): TUserInterfaceState => {
      return {
        ...state,
        nextStepDisabled: false,
        order: {
          ...state.order,
          datetime: {
            ...state.order.datetime,
            time: action.payload.time,
          },
          eventId: action.payload.eventId,
          event: action.payload.event,
        },
      };
    },
    setNote: (
      state: TUserInterfaceState,
      action: PayloadAction<TSetNote>,
    ): TUserInterfaceState => {
      return {
        ...state,
        order: {
          ...state.order,
          note: action.payload.note,
        },
      };
    },
    setCurrentStep: (
      state: TUserInterfaceState,
      action: PayloadAction<TSetCurrentStep>,
    ) => ({
      ...state,
      currentStep: action.payload.step,
    }),
    setNextStepDisabled: (
      state: TUserInterfaceState,
      action: PayloadAction<TSetNextStepDisabled>,
    ) => ({
      ...state,
      nextStepDisabled: action.payload.disabled,
    }),
    moveToPrevStep: (state: TUserInterfaceState) => ({
      ...state,
      currentStep: state.currentStep - 1,
    }),
    moveToNextStep: (state: TUserInterfaceState) => ({
      ...state,
      currentStep: state.currentStep + 1,
    }),
    setWidgetOpen: (
      state: TUserInterfaceState,
      action: PayloadAction<TSetWidgetOpen>,
    ): TUserInterfaceState => ({
      ...state,
      widgetOpen: action.payload.open,
    }),
    setLanguage: (
      state: TUserInterfaceState,
      action: PayloadAction<TSetLanguage>,
    ): TUserInterfaceState => ({
      ...state,
      currentLang: action.payload.language,
    }),
    resetEventId: (state: TUserInterfaceState): TUserInterfaceState => ({
      ...state,
      order: {
        ...state.order,
        eventId: null,
      },
    }),
    setDayClicked: (
      state: TUserInterfaceState,
      action: PayloadAction<boolean>,
    ): TUserInterfaceState => ({
      ...state,
      dayClicked: action.payload,
    }),
  },
});

export default userInterfaceSlice;
